import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { ImageSourcePropType } from 'react-native';
import { View, StyleSheet } from 'react-native';

import { theme } from '@garnish/constants';
import { Image } from '../Image';
import { BodyText } from '../Text';
import { Card } from '../Card';

export const RewardCard = (props: RewardCardProps) => {
  const {
    id,
    description,
    isSelected,
    deadline,
    onPress,
    imageSource,
    ...restProps
  } = props;

  // ─────────────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.(id);
  }, [id, onPress]);

  const onPressHandler = onPress ? handleOnPress : undefined;

  // ─────────────────────────────────────────────────────────────────

  return (
    <Card
      testID={`sg-add-on-card-${id}`}
      style={styles.cardContainer}
      accessibilityLabel={description}
      accessibilityRole="button"
      onPress={onPressHandler}
      isSelected={isSelected}
      {...restProps}
    >
      <View style={styles.cardInner}>
        <Image
          source={imageSource}
          resizeMode="cover"
          accessibilityIgnoresInvertColors
          style={styles.illustration}
        />

        <View style={styles.infoContainer}>
          <View style={styles.descriptionWrapper}>
            <BodyText numberOfLines={3} ellipsizeMode="tail" size={4}>
              {description}
            </BodyText>
          </View>

          {deadline ? (
            <BodyText
              testID="sg-reward-card-deadline"
              size={4}
              style={styles.deadline}
            >
              {deadline}
            </BodyText>
          ) : null}
        </View>
      </View>
    </Card>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardContainer: {
    minHeight: 120,
    paddingHorizontal: theme.spacing.medium,
    borderRadius: theme.radius.large,
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardInner: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  infoContainer: {
    flex: 1,
    paddingLeft: theme.spacing.medium,
  },
  descriptionWrapper: {
    paddingBottom: theme.spacing.small,
  },
  deadline: {
    color: theme.colors.CHARCOAL,
  },
  illustration: {
    width: 80,
    height: 80,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type RewardCardProps = {
  readonly id: string;
  readonly imageSource: ImageSourcePropType;
  readonly description: string;
  readonly deadline?: string;
  readonly onPress?: (id: string) => void;
} & Omit<ComponentProps<typeof Card>, 'onPress'>;
